import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { debounce } from 'underscore';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import { formatPrice } from 'utils/common';
import i18n from 'utils/translate';
import Checkbox from 'components/Checkbox';
import NavigationButtons from 'components/NavigationButtons';
import Section from 'components/Section';
import TextField from 'components/TextField';
import InfoTextHeader from 'components/InfoTextHeader';
import './styles.scss';
import { toast } from 'react-toastify';

import { CALC_API_DEBOUNCE_DURATION, DATE_ERROR_MESSAGES } from '../../constants';
import { calculateEndDate, numberToLocaleString } from '../../utils';
import ErrorMessage from 'components/ErrorMessage';
import { linkTypeShape } from 'utils/types';

const propTypes = {
  calculateQuote: PropTypes.func.isRequired,
  backLink: PropTypes.shape(linkTypeShape),
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  onChange: PropTypes.func.isRequired
};

const defaultProps = { onChange: console.log };

const keyPriceFormatted = 'price_currency';
const maxKaufpreis = 10000;

class BikeDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ebike: false,
      infoAccept: false,
      versicherungsbeginn: moment().add(1, 'day'),
      ...props.data,
      submitted: false,
      validForm: false,
      showMaxValError: false,
      wrongBirthDate: null,
      wrongBuiltDate: null,
      wrongInsuranceDate: null
    };
  }

  componentDidMount() {
    this.calcQuote();
    animateScroll.scrollToTop({ duration: 0 });
  }

  handleReverse = e => {
    const {
      backLink: { go } = {
        go: () => {}
      }
    } = this.props;
    e.preventDefault();
    e.stopPropagation();
    go();
  };

  handleSubmit = e => {
    const {
      nextLink: { go } = {
        go: () => {}
      },
      onChange = () => {}
    } = this.props;
    const form = this.refs.form;

    if (form.checkValidity && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    onChange(this.state);
    go();

    window.dataLayer.push({
      event: 'onlineabschluss',
      formname: 'Fahrraddiebstahl-Versicherung',
      action: 'Schritt 1 von 4'
    });
  };

  calcQuote() {
    const { kaufpreis } = this.state;

    if (kaufpreis !== undefined && kaufpreis <= maxKaufpreis) {
      this.requestQuote();
    }
  }

  requestQuote = debounce(() => {
    const { calculateQuote } = this.props;
    const { kaufpreis } = this.state;

    if (kaufpreis !== undefined && kaufpreis <= maxKaufpreis) {
      calculateQuote({
        ...this.state
      }).then(quote => {
        this.setState({ quote });
      });
    }
  }, CALC_API_DEBOUNCE_DURATION);

  handleDataChange = (data, key) => {
    const { submitted } = this.state;
    const { onChange } = this.props;

    if (key === keyPriceFormatted) {
      const price_numeric = this.refs.price.autonumeric.getNumber();

      this.setState({
        showMaxValError: price_numeric > maxKaufpreis,
        kaufpreis: price_numeric
      });
    }

    this.setState(
      {
        [`${key}`]: data
      },
      () => {
        this.calcQuote();
        onChange(this.state);
        if (submitted) {
          this.setState({ validForm: this.refs.form.checkValidity() });
        }
      }
    );
  };

  handleEBikeChange = ({ target: { checked: ebike } }) => {
    this.setState({ ebike });
    this.calcQuote();
  };

  handleInfoAccept = ({ target: { checked: value } }) => {
    const { submitted } = this.state;

    this.setState({ infoAccept: value });
    if (submitted) {
      this.setState({ validForm: this.refs.form.checkValidity() });
    }
  };

  render() {
    const { backLink } = this.props;
    const {
      quote,
      ebike,
      baujahr,
      price_currency,
      rahmennummer,
      markeModell,
      versicherungsbeginn,
      geburtsdatum,
      submitted,
      showMaxValError,
      wrongBirthDate,
      wrongBuiltDate,
      wrongInsuranceDate,
      downloadedBasicInfo,
      infoAccept
    } = this.state;
    const basicInfoDownloadClass = submitted && !downloadedBasicInfo ? 'download-error' : '';
    const basicInfoLink = window.documentLinks ? window.documentLinks.basicInfos : '';

    let price = null;
    let currency = null;

    if (quote) {
      price = quote.price;
      currency = quote.currency;
    }

    // birthday
    let birtdayError = '';
    let maxDate = moment().subtract(14, 'year');

    if (moment.isMoment(wrongBirthDate) && wrongBirthDate.isValid() && wrongBirthDate.isAfter(maxDate)) {
      if (wrongBirthDate.isAfter(moment())) {
        birtdayError = DATE_ERROR_MESSAGES.inFuture;
      } else {
        birtdayError = DATE_ERROR_MESSAGES.min14years;
      }
    }

    // built date
    let builtError = '';
    let builtMaxDate = moment().subtract(1, 'day');

    if (
      moment.isMoment(wrongBuiltDate) &&
      wrongBuiltDate.isValid() &&
      wrongBuiltDate.isAfter(builtMaxDate) &&
      wrongBuiltDate.isAfter(moment())
    ) {
      builtError = DATE_ERROR_MESSAGES.inFuture;
    }

    // insurance duration
    let insuranceError = '';
    let insuranceMinStartDate = moment();
    let insuranceMaxStartDate = moment().add(90, 'day');
    const insuranceEndDate = calculateEndDate(versicherungsbeginn);

    if (moment.isMoment(wrongInsuranceDate) && wrongInsuranceDate.isValid()) {
      if (wrongInsuranceDate.isBefore(insuranceMinStartDate)) {
        insuranceError = DATE_ERROR_MESSAGES.inPast;
      } else if (wrongInsuranceDate.isAfter(insuranceMaxStartDate)) {
        insuranceError = DATE_ERROR_MESSAGES.max90daysInFuture;
      }
    }

    return (
      <form
        ref="form"
        onSubmit={this.handleSubmit}
        className={`page-step-wrapper bike-details page-step-wrapper--narrow ${submitted ? 'submitted' : ''}`}
        id="insurance-form"
      >
        <Section header="Information zum Produktvertreiber">
          <InfoTextHeader checked={infoAccept} onChange={value => this.handleInfoAccept(value)} />
        </Section>
        <Section header={i18n('Details zum Fahrrad')}>
          <div className="row">
            <TextField
              label={i18n('Marke und Modell')}
              name="make_and_model"
              className="col-xs-12 col-md-6"
              value={markeModell}
              required
              onChange={data => this.handleDataChange(data, 'markeModell')}
              ref="brand"
            />
            <TextField
              label={i18n('Rahmennummer')}
              name="frame_number"
              className="col-xs-12 col-md-4"
              value={rahmennummer}
              required
              questionMarkImage="Infobild-Rahmennummer.jpg"
              onChange={data => this.handleDataChange(data, 'rahmennummer')}
              ref="number"
            />
            <Checkbox
              className="col-xs-12 col-md-2"
              id="ebikeCheckbox"
              name="ebike"
              checked={ebike}
              onChange={this.handleEBikeChange}
              label={i18n('E-Bike')}
            />
          </div>
          <div className="row">
            <TextField
              label={i18n('Neupreis des Fahrrades (inklusive Zubehör)')}
              name="price"
              className="col-xs-12 col-md-6"
              value={price_currency}
              required
              onChange={data => this.handleDataChange(data, 'price_currency')}
              ref="price"
              customError={
                showMaxValError ? `Neupreis darf maximal € ${numberToLocaleString(maxKaufpreis)} betragen!` : null
              }
              currencyInput
            />
            <TextField
              label={i18n('Baujahr')}
              name="dateBuilt"
              className="col-xs-12 col-md-2"
              date
              required
              placeholder={i18n('JJJJ')}
              value={baujahr}
              maxDate={builtMaxDate}
              onChange={data => this.handleDataChange(data, 'baujahr')}
              onWrongDate={wrongBuiltDate => this.setState({ wrongBuiltDate })}
              customError={builtError}
              dateFormat="YYYY"
              ref="dateBuilt"
            />
          </div>
        </Section>
        <Section header={i18n('Versicherungsdauer')}>
          <TextField
            label={i18n('Versicherungsbeginn')}
            name="start_date"
            className="col-xs-12 col-md-6 col-lg-4"
            required
            date
            placeholder={i18n('TT.MM.JJJJ')}
            value={versicherungsbeginn}
            minDate={insuranceMinStartDate}
            maxDate={insuranceMaxStartDate}
            onChange={data => this.handleDataChange(data, 'versicherungsbeginn')}
            onWrongDate={wrongInsuranceDate => this.setState({ wrongInsuranceDate })}
            customError={insuranceError}
            ref="startDate"
          />
          <TextField
            label={i18n('Versicherungsende/Folgefälligkeit')}
            name="end_date"
            className="col-xs-12 col-md-6 col-lg-4"
            value={insuranceEndDate}
            questionMark={
              'Sollte der Vertrag nicht 3 Monate vor Versicherungsende schriftlich gekündigt werden, verlängert er sich automatisch um ein weiteres Jahr.'
            }
            disabled
          />
        </Section>
        <Section header={i18n('Details zur versicherten Person')}>
          <TextField
            label={i18n('Geburtsdatum')}
            name="last_name"
            className="col-xs-12 col-md-4"
            required
            value={geburtsdatum}
            maxDate={maxDate}
            date
            placeholder={i18n('TT.MM.JJJJ')}
            onChange={data => this.handleDataChange(data, 'geburtsdatum')}
            onWrongDate={wrongBirthDate => this.setState({ wrongBirthDate })}
            customError={birtdayError}
            ref="date"
          />
          <span className="col-xs-12 birthdate-info">
            {i18n(
              'Alle Kunden unter 25 erhalten bei einem Fahrrad-Neupreis bis € 3.000,- unseren vergünstigten GRAWE sidebyside Jugendtarif.'
            )}
          </span>
        </Section>

        <div className="row">
          {versicherungsbeginn && (
            <div className="col-xs-12 hint">
              {i18n(
                `Wir empfehlen Ihnen aufgrund Ihrer Angaben unsere Fahrraddiebstahl-Versicherung mit einer Versicherungsdauer von ${versicherungsbeginn.format(
                  'DD.MM.YYYY'
                )} bis ${insuranceEndDate}.`
              )}
            </div>
          )}
        </div>

        <div className="row">
          <div className="bib-wrapper col-xs-12">
            <p>
              {i18n(
                'Die standardisierten Produkt- bzw. Basisinformationsblätter beinhalten wichtige Informationen zur Fahrraddiebstahl-Versicherung. Bitte nehmen Sie sich ausreichend Zeit und lesen Sie diese sorgfältig.'
              )}
            </p>
            <a
              className="col-xs-12 basicInfos-pdf"
              href={basicInfoLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => this.handleDataChange(true, 'downloadedBasicInfo')}
            >
              <span className="pdf-icon col-xs-2" />
              <span className={`col-xs-10 ${basicInfoDownloadClass}`}>
                {i18n('Download der standardisierten Produkt- bzw. Basisinformationsblätter')}
                <span className="field-error-message">
                  {i18n(
                    'Die standardisierten Produkt- bzw. Basisinformationsblätter sind verpflichtend herunterzuladen, wenn Sie überlegen, die Fahrraddiebstahl-Versicherung online abzuschließen.'
                  )}
                </span>
              </span>
            </a>
            <p className="p-margin-top">
              {i18n(
                'Sie können dieses Dokument sowie sämtliche vorvertragliche Informationen auch in Papierform erhalten.'
              )}
              <br />
              {i18n('Bitte senden Sie dazu ein Mail mit Ihrer Postanschrift an ')}
              <a href="mailto:fahrrad.center.vs@grawe.at@grawe.at?subject=Postversand Vorvertragliche Informationen">
                fahrrad.center.vs@grawe.at
              </a>
              .
              <br />
              {i18n('Nach Erhalt der Dokumente können Sie mit dem Online-Vertragsabschluss fortfahren.')}
            </p>
          </div>
        </div>

        <div className="sticky-footer">
          <div className="page-step-wrapper__price">
            {i18n('Prämie (jährlich)')}&nbsp;
            {formatPrice(price, currency)}
          </div>

          <NavigationButtons
            nextLink={{
              go: () => {
                this.setState({
                  submitted: true,
                  validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
                });

                Object.keys(this.refs).forEach(key => {
                  if (this.refs[key].onBlur) {
                    this.refs[key].onBlur();
                  }
                });

                if (
                  !showMaxValError &&
                  quote &&
                  quote.price &&
                  downloadedBasicInfo &&
                  this.refs.form.checkValidity &&
                  this.refs.form.checkValidity()
                ) {
                  toast.dismiss();
                  this.handleSubmit();
                } else {
                  toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                }
              }
            }}
            backLink={backLink}
          />
        </div>
      </form>
    );
  }
}

BikeDetails.defaultProps = defaultProps;
BikeDetails.propTypes = propTypes;

export default BikeDetails;
